import {
  ChangeDetectionStrategy,
  Component,
  input,
  signal,
  inject,
  DestroyRef,
  PLATFORM_ID,
  computed,
  afterNextRender,
} from '@angular/core';
import {CommonModule, isPlatformBrowser} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {interval, Subject, switchMap} from 'rxjs';
import {UmpireArticle, UmpireComponentMainHeroArticles} from '@tisa-group/models';
import {RouterLink} from '@angular/router';
import {TISA_CURRENT_LOCALE} from '@tisa-group/client-ui/tokens';

@Component({
  selector: 'tisa-hero-main-articles',
  imports: [CommonModule, RouterLink],
  templateUrl: './hero-main-articles.component.html',
  styleUrl: './hero-main-articles.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeroMainArticlesComponent {
  private readonly destroyRef = inject(DestroyRef);

  private readonly platformId = inject(PLATFORM_ID);

  private readonly isBrowser = isPlatformBrowser(this.platformId);

  private readonly restartTimer$ = new Subject<void>();

  readonly locale = inject(TISA_CURRENT_LOCALE);

  readonly component = input<UmpireComponentMainHeroArticles | undefined>();

  readonly activeIndex = signal(0);

  readonly articles = computed(() => this.component()?.articles.slice(0, 3) || []);

  readonly currentArticle = computed(
    () => this.articles()[this.activeIndex()] || this.articles()[0],
  );

  readonly hasMultipleArticles = computed(() => this.articles().length > 1);

  constructor() {
    // Auto-rotation effect - only runs after client hydration
    afterNextRender(() => {
      if (!this.isBrowser || !this.hasMultipleArticles()) {
        return;
      }

      // Start auto-rotation when component initializes or restarts
      this.restartTimer$
        .pipe(
          switchMap(() => interval(10000)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => this.nextArticle());

      // Initial start
      this.restartTimer$.next();
    });
  }

  private restartAutoRotation(): void {
    this.restartTimer$.next();
  }

  trackByArticleId(_index: number, article: UmpireArticle): string {
    return article.id;
  }

  setActiveArticle(index: number): void {
    const articles = this.articles();
    if (index < 0 || index >= articles.length) return;
    // Only update and restart timer if switching to a different article
    if (index !== this.activeIndex()) {
      this.activeIndex.set(index);
      this.restartAutoRotation();
    }
  }

  private nextArticle(): void {
    const articles = this.articles();
    if (articles.length <= 1) return;
    const nextIndex = (this.activeIndex() + 1) % articles.length;
    this.activeIndex.set(nextIndex);
  }
}
